import { endSession } from './agent';
import { CreatorApiError, createEnvelopeClient, requireData } from './creatorApiAgent';
import { billingApiBaseURL } from './billingApiConfig';
import { isSessionSuperseded } from './sessionSuperseded';
import type {
  TAdminAddMemberRequest,
  TAdminCompany,
  TAdminCompanyDetail,
  TAdminCreateCompanyRequest,
  TBuyQuotaPackageRequest,
  TBuyQuotaPackageResponse,
  TCompany,
  TCompaniesFound,
  TPurchaseForm,
} from '../models/BillingModels';

/**
 * `CreatorBilling.API`: the quota packages on sale and buying one, under its `QuotaPackages`
 * controller, the company register lookup under `Companies`, and the admin panel's companies under
 * `Admin/Companies`, at its own base URL. The envelope
 * plumbing is the creator API's, built for this host by `createEnvelopeClient`.
 *
 * Knows nothing about the store or React.
 */

const billingApi = createEnvelopeClient(billingApiBaseURL);

const SERVICE_UNREACHABLE_MESSAGE = 'The billing service could not be reached. Please try again.';

const SIGN_IN_NOT_ACCEPTED_MESSAGE = 'The billing service did not accept your sign-in. Please reload the page and try again.';

/** The host answers 401 when the token it was sent signs nobody in. */
const HTTP_UNAUTHORIZED = 401;

/**
 * Everything this agent rejects with is fit to put in front of an author. A 401 that says the session
 * was superseded ends it here as it does on the creator host (`isSessionSuperseded`, `endSession`), so
 * the request never settles; any other 401 is an ordinary request failure, carrying its status. A
 * refusal the backend wrote — a bad organisation number, a refused invoice, "try again shortly" —
 * arrives as a `CreatorApiError` and is passed on untouched. Any other request, answered or not, has
 * only the transport's words, which go to the log, and reads as unreachable on screen.
 */
const asAuthorFacing = (error: unknown): Promise<never> => {
  if (error instanceof CreatorApiError) throw error;
  const response = (error as { response?: { status?: number } } | null)?.response;
  if (isSessionSuperseded(response)) return endSession();
  if (response?.status === HTTP_UNAUTHORIZED) throw new CreatorApiError(SIGN_IN_NOT_ACCEPTED_MESSAGE, HTTP_UNAUTHORIZED);

  console.warn('The billing request failed before the backend answered', error);
  throw new Error(SERVICE_UNREACHABLE_MESSAGE);
};

/** What an envelope request answers, its data required, its failures made fit for an author (`asAuthorFacing`). */
const dataOf = <T>(request: Promise<T | null>): Promise<T> => request.catch(asAuthorFacing).then(requireData);

const QuotaPackages = {
  /**
   * What the purchase form opens with for the signed-in owner (the impersonated one while a
   * superAdmin works as someone else): the packages on sale and the details to prefill. Asked with the
   * resolved company's `orgNumber`, the prefill also says the invoice email that company keeps on file.
   */
  getPurchaseForm: (orgNumber?: string): Promise<TPurchaseForm> =>
    dataOf(billingApi.requests.getEnvelope<TPurchaseForm>(orgNumber === undefined ? '/QuotaPackages/form' : `/QuotaPackages/form?orgNumber=${encodeURIComponent(orgNumber)}`)),

  /**
   * Buys one package for a company. Resolves when the order stands — accepted, or pending until the
   * invoice has been handed on — and rejects in the backend's words for every refusal.
   */
  buy: (request: TBuyQuotaPackageRequest): Promise<TBuyQuotaPackageResponse> => dataOf(billingApi.requests.postEnvelope<TBuyQuotaPackageResponse>('/QuotaPackages/buy', request)),
};

const Companies = {
  /**
   * The registered companies `q` names — by organisation number when it is nine digits, by name
   * otherwise — as Brønnøysundregistrene has them. Rejects in the backend's words when the register
   * cannot be reached.
   */
  lookup: (q: string): Promise<TCompany[]> => dataOf(billingApi.requests.getEnvelope<TCompaniesFound>(`/Companies?q=${encodeURIComponent(q)}`)).then((lookup) => lookup.companies),
};

/** One company's route under `Admin/Companies`, by its Company party id. */
const adminCompanyPath = (id: number) => `/Admin/Companies/${encodeURIComponent(String(id))}`;

/**
 * The admin panel's Companies tab: every company, and creating, changing, closing, reopening and
 * deleting one. superAdmin only; the backend answers 403 to anyone else. Every change answers the
 * company as `get` does; a refusal rejects as a `CreatorApiError` carrying the backend's message and
 * its `reactName` (`TAdminCompanyErrorKind`).
 */
const AdminCompanies = {
  /** `GET Admin/Companies`: every company, open and closed, in creation order. */
  list: (): Promise<TAdminCompany[]> => dataOf(billingApi.requests.getEnvelope<TAdminCompany[]>('/Admin/Companies')),

  /** `GET Admin/Companies/{id}`: one company with its members. */
  get: (id: number): Promise<TAdminCompanyDetail> => dataOf(billingApi.requests.getEnvelope<TAdminCompanyDetail>(adminCompanyPath(id))),

  /** `POST Admin/Companies`: a new company for a number the register knows, with its name from there. */
  create: (request: TAdminCreateCompanyRequest): Promise<TAdminCompanyDetail> =>
    dataOf(billingApi.requests.postEnvelope<TAdminCompanyDetail>('/Admin/Companies', request)),

  /** `PUT Admin/Companies/{id}/invoiceEmail`. */
  setInvoiceEmail: (id: number, invoiceEmail: string): Promise<TAdminCompanyDetail> =>
    dataOf(billingApi.requests.putEnvelope<TAdminCompanyDetail>(`${adminCompanyPath(id)}/invoiceEmail`, { invoiceEmail })),

  /** `POST Admin/Companies/{id}/refreshName`: the register's name now, written over if it differs. */
  refreshName: (id: number): Promise<TAdminCompanyDetail> =>
    dataOf(billingApi.requests.postEnvelope<TAdminCompanyDetail>(`${adminCompanyPath(id)}/refreshName`)),

  /** `POST Admin/Companies/{id}/close`: closed, and every open membership to it closed with it. */
  close: (id: number): Promise<TAdminCompanyDetail> => dataOf(billingApi.requests.postEnvelope<TAdminCompanyDetail>(`${adminCompanyPath(id)}/close`)),

  /** `POST Admin/Companies/{id}/reopen`: open again; its memberships stay closed. */
  reopen: (id: number): Promise<TAdminCompanyDetail> => dataOf(billingApi.requests.postEnvelope<TAdminCompanyDetail>(`${adminCompanyPath(id)}/reopen`)),

  /** `DELETE Admin/Companies/{id}`: gone, answered as it was. Refused while it has a purchase or a membership. */
  delete: (id: number): Promise<TAdminCompanyDetail> => dataOf(billingApi.requests.deleteEnvelope<TAdminCompanyDetail>(adminCompanyPath(id))),

  /**
   * `POST Admin/Companies/{id}/members`: makes the owner behind an Auth0 user id a member. A member of
   * another company is refused with `wouldMove` unless `confirmMove` is true, which moves them.
   */
  addMember: (id: number, request: TAdminAddMemberRequest): Promise<TAdminCompanyDetail> =>
    dataOf(billingApi.requests.postEnvelope<TAdminCompanyDetail>(`${adminCompanyPath(id)}/members`, request)),

  /** `DELETE Admin/Companies/{id}/members/{ownerId}`: closes the owner's membership to the company. */
  removeMember: (id: number, ownerId: number): Promise<TAdminCompanyDetail> =>
    dataOf(billingApi.requests.deleteEnvelope<TAdminCompanyDetail>(`${adminCompanyPath(id)}/members/${encodeURIComponent(String(ownerId))}`)),
};

const billingApiAgent = {
  QuotaPackages,
  Companies,
  AdminCompanies,
};

export default billingApiAgent;
